/**
 * Vocabulary Sets Store
 *
 * Themed word sets (colours, months, body parts) — a second route to the same
 * senses role play already teaches.
 *
 * The store caches responses and nothing else. It does **not** compute mastery:
 * a member's status is the shared `UserVocabUsage` row (VSET-002), the mark
 * response returns the new status, and predicting it locally would be the
 * client re-deriving the mastery rule (VSM-007).
 *
 * Deliberately the same shape as `competenceMapStore` — load-on-focus, keep
 * stale data on a failed refresh, individual selectors per CHAT_PERFORMANCE.md.
 *
 * @see docs/specs/mobile/vocab-sets.mobile.md
 * @see docs/specs/api/vocab-sets.api.md
 */

import { create } from 'zustand';
import { api } from '../services/api';
import { createLogger } from '../utils/logger';
import type { StoreErrorInfo } from '../generated/api/endpoint-errors';
import type {
  LearnerStatus,
  VocabSetDetailResponse,
  VocabSetSummary,
} from '../generated/api/http-contracts';

const log = createLogger('VocabSetsStore');

/** What the learner did with one word. Only the middle one is production. */
export type DrillEvent = 'produced' | 'encountered' | 'struggled';

interface VocabSetsState {
  sets: VocabSetSummary[];
  /** Detail of the set currently open, keyed by slug so a back-and-forth
   *  between two sets does not show the previous one's words for a frame. */
  detail: Record<string, VocabSetDetailResponse>;
  loading: boolean;
  detailLoading: boolean;
  errorInfo: StoreErrorInfo | null;
  /** Set when a mark fails. The drill must NOT advance on this (VSM-008). */
  markErrorInfo: StoreErrorInfo | null;

  loadSets: (courseId: string) => Promise<void>;
  loadSet: (courseId: string, slug: string) => Promise<void>;
  /** Returns the new status, or null if the mark did not reach the server. */
  mark: (
    courseId: string,
    slug: string,
    senseId: string,
    event: DrillEvent,
  ) => Promise<LearnerStatus | null>;
  clearError: () => void;
}

export const useVocabSetsStore = create<VocabSetsState>((set, get) => ({
  sets: [],
  detail: {},
  loading: false,
  detailLoading: false,
  errorInfo: null,
  markErrorInfo: null,

  loadSets: async (courseId) => {
    if (get().loading) return;
    set({ loading: true, errorInfo: null });
    const result = await api.getVocabSets({ params: { courseId } });
    if (result.ok) {
      set({ sets: result.data.sets, loading: false });
    } else {
      // Keep whatever we had: a failed refresh is non-fatal for a list the
      // learner is already looking at.
      log.warn('Failed to load vocab sets', { kind: result.kind });
      set({ loading: false, errorInfo: result });
    }
  },

  loadSet: async (courseId, slug) => {
    set({ detailLoading: true, errorInfo: null });
    const result = await api.getVocabSet(slug, { params: { courseId } });
    if (result.ok) {
      set((s) => ({ detail: { ...s.detail, [slug]: result.data }, detailLoading: false }));
    } else {
      log.warn('Failed to load vocab set', { slug, kind: result.kind });
      set({ detailLoading: false, errorInfo: result });
    }
  },

  mark: async (courseId, slug, senseId, event) => {
    set({ markErrorInfo: null });
    const result = await api.markVocabSetItem(slug, { courseId, senseId, event });
    if (!result.ok) {
      // Surface it and change nothing. Advancing anyway would show the learner
      // progress the server never recorded (VSM-008).
      log.warn('Failed to mark vocab set item', { slug, senseId, event, kind: result.kind });
      set({ markErrorInfo: result });
      return null;
    }

    const { status } = result.data;
    // Apply the SERVER's status, never a locally predicted one (VSM-007), and
    // patch the cached counts so the list does not lie until the next fetch.
    set((s) => {
      const d = s.detail[slug];
      if (!d) return { markErrorInfo: null };
      const members = d.members.map((m) => (m.senseId === senseId ? { ...m, status } : m));
      const progress = countStatuses(members.map((m) => m.status));
      return {
        markErrorInfo: null,
        detail: { ...s.detail, [slug]: { ...d, members, set: { ...d.set, progress } } },
        sets: s.sets.map((x) => (x.slug === slug ? { ...x, progress } : x)),
      };
    });
    return status;
  },

  clearError: () => set({ errorInfo: null, markErrorInfo: null }),
}));

/**
 * `new` is the remainder, not a count — so the three always sum to the total
 * and a learner never sees a set whose parts disagree with its size (VSET-009).
 */
function countStatuses(statuses: LearnerStatus[]): VocabSetSummary['progress'] {
  let mastered = 0;
  let learning = 0;
  for (const s of statuses) {
    if (s === 'mastered') mastered++;
    else if (s === 'learning') learning++;
  }
  return { total: statuses.length, mastered, learning, new: statuses.length - mastered - learning };
}

// ─── Selectors (individual — CHAT_PERFORMANCE.md rule) ──────────────

export const selectVocabSets = (s: VocabSetsState) => s.sets;
export const selectVocabSetsLoading = (s: VocabSetsState) => s.loading;
export const selectVocabSetDetailLoading = (s: VocabSetsState) => s.detailLoading;
export const selectVocabSetsErrorInfo = (s: VocabSetsState) => s.errorInfo;
export const selectVocabSetMarkErrorInfo = (s: VocabSetsState) => s.markErrorInfo;
export const selectLoadVocabSets = (s: VocabSetsState) => s.loadSets;
export const selectLoadVocabSet = (s: VocabSetsState) => s.loadSet;
export const selectMarkVocabSetItem = (s: VocabSetsState) => s.mark;
export const selectClearVocabSetsError = (s: VocabSetsState) => s.clearError;
